import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoLockup } from "./Logo";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const isActive = (to) =>
    to === "/" ? pathname === "/" : pathname.startsWith(to);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-background/85 border-b border-border"
          : "bg-transparent"
      }`}
    >
      <nav className="max-page container-px h-20 flex items-center justify-between gap-8">
        <Link
          to="/"
          className="group flex items-center"
          data-testid="nav-logo"
          aria-label="One Sphere — Home"
        >
          <LogoLockup
            height={42}
            className="transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <ul className="hidden lg:flex items-center gap-10 mx-auto">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`text-sm tracking-wide transition-colors relative group ${
                  isActive(n.to) ? "text-foreground" : "text-foreground/70 hover:text-foreground"
                }`}
              >
                {n.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-primary transition-all duration-500 ${
                    isActive(n.to) ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center">
          <button
            onClick={() => navigate("/contact")}
            data-testid="nav-cta-quote"
            className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-sm tracking-wide hover:bg-foreground transition-all duration-500"
          >
            Request a Quote
            <span className="transition-transform duration-500 group-hover:translate-x-1">↗</span>
          </button>
        </div>

        <button
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden p-2 text-foreground"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <ul className="container-px py-6 space-y-4">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                  className="block text-base text-foreground/80 hover:text-foreground"
                >
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/contact"
                data-testid="nav-mobile-cta"
                className="inline-block mt-2 bg-primary text-primary-foreground px-5 py-3 rounded-full text-sm"
              >
                Request a Quote
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
